/**
 * AI Chat panel for the sidebar -- agentic tool-calling loop.
 *
 * In SaaS mode, renders a full AI chat powered by Gemini with tool calling.
 * The AI can list, read, edit, and create scenes, plus access audio analysis.
 * Mirrors the agentic loop from packages/ai/src/editor/Provider.tsx.
 */

import {useCallback, useEffect, useRef, useState} from 'react';
import {Pane} from '../tabs';
import styles from './Threads.module.scss';

import {useSaaSEditor} from '../../saas/contexts/editor-context';
import {
  buildScenePrompt,
  buildContinuationPrompt,
  buildAudioSummary,
} from '../../saas/prompt';
import {
  SAAS_TOOL_DECLARATIONS,
  executeSaaSTool,
  type ToolContext,
  type ToolResult,
} from '../../saas/ai-tools';
import type {Scene} from '../../saas/db';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ToolActivity {
  id: string;
  toolName: string;
  status: 'running' | 'done' | 'error';
  summary: string;
}

type ContentBlock =
  | {type: 'text'; text: string}
  | {type: 'activity'; activity: ToolActivity};

interface ChatMessage {
  id: string;
  role: 'user' | 'ai' | 'error' | 'success';
  content: string;
  blocks?: ContentBlock[];
}

/** A function call parsed from the SSE stream. */
interface ParsedFunctionCall {
  id: string;
  name: string;
  args: Record<string, unknown>;
  /** Gemini 3.x thought signature for reasoning continuity across tool calls. */
  thoughtSignature?: string;
}

/** A message in the Gemini conversation history. */
interface GeminiMessage {
  role: 'user' | 'model' | 'function';
  parts: Array<Record<string, unknown>>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_ITERATIONS = 15;
const COMPRESS_THRESHOLD = 800;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

let idCounter = 0;
function nextId(prefix = 'msg'): string {
  return `${prefix}-${Date.now()}-${++idCounter}`;
}

/** Build a human-readable summary for a running tool. */
function runningSummary(name: string, args: Record<string, unknown>): string {
  switch (name) {
    case 'read_scene':
      return args.name ? `Reading "${args.name}"...` : 'Reading scene...';
    case 'edit_scene':
      return args.name ? `Editing "${args.name}"...` : 'Editing scene...';
    case 'rewrite_scene':
      return args.name ? `Rewriting "${args.name}"...` : 'Rewriting scene...';
    case 'create_scene':
      return args.name ? `Creating "${args.name}"...` : 'Creating scene...';
    case 'list_scenes':
      return 'Listing scenes...';
    case 'get_audio_analysis':
      return 'Getting audio analysis...';
    case 'get_project_info':
      return 'Getting project info...';
    default:
      return `Running ${name}...`;
  }
}

/** Build a done summary from the tool result. */
function doneSummary(
  name: string,
  args: Record<string, unknown>,
  result: ToolResult,
): string {
  if (result.isError) return `Error: ${result.content.slice(0, 120)}`;
  switch (name) {
    case 'read_scene':
      return args.name
        ? `Read "${args.name}" (${result.content.length.toLocaleString()} chars)`
        : 'Read scene';
    case 'edit_scene':
      return args.name ? `Edited "${args.name}"` : 'Edited scene';
    case 'rewrite_scene':
      return args.name ? `Rewrote "${args.name}"` : 'Rewrote scene';
    case 'create_scene':
      return args.name ? `Created "${args.name}"` : 'Created scene';
    case 'list_scenes':
      return 'Listed scenes';
    case 'get_audio_analysis':
      return 'Got audio analysis';
    case 'get_project_info':
      return 'Got project info';
    default:
      return `Completed ${name}`;
  }
}

/**
 * Compress tool results older than current iteration to save context.
 * Replaces large functionResponse contents with a short summary.
 */
function compressOldToolResults(messages: GeminiMessage[]): void {
  for (const msg of messages) {
    if (msg.role !== 'function') continue;
    for (const part of msg.parts) {
      const resp = part.functionResponse as
        | {name: string; response: {content: string}}
        | undefined;
      if (resp?.response?.content && resp.response.content.length > COMPRESS_THRESHOLD) {
        resp.response.content = `[Tool result: ${resp.response.content.length.toLocaleString()} chars. Processed in earlier turn, compressed.]`;
      }
    }
  }
}

// ---------------------------------------------------------------------------
// SSE streaming client
// ---------------------------------------------------------------------------

interface StreamResult {
  text: string;
  functionCalls: ParsedFunctionCall[];
}

async function streamGeminiResponse(
  systemPrompt: string,
  messages: GeminiMessage[],
  tools: typeof SAAS_TOOL_DECLARATIONS,
  signal: AbortSignal,
  onTextChunk: (fullText: string) => void,
): Promise<StreamResult> {
  const res = await fetch('/api/chat', {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({
      systemPrompt,
      messages,
      tools: [{functionDeclarations: tools}],
      generationConfig: {temperature: 0.5, maxOutputTokens: 65536},
    }),
    signal,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({error: res.statusText}));
    throw new Error(body.error ?? `API error ${res.status}`);
  }

  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let fullText = '';
  const functionCalls: ParsedFunctionCall[] = [];

  while (true) {
    const {done, value} = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, {stream: true});
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith('data: ')) continue;
      const data = trimmed.slice(6);
      if (data === '[DONE]') continue;

      try {
        const parsed = JSON.parse(data);

        if (parsed.type === 'text' && parsed.text) {
          fullText += parsed.text;
          onTextChunk(fullText);
        } else if (parsed.type === 'functionCall') {
          functionCalls.push({
            id: parsed.id,
            name: parsed.name,
            args: parsed.args ?? {},
            thoughtSignature: parsed.thoughtSignature,
          });
        } else if (parsed.type === 'error') {
          throw new Error(parsed.error);
        }
        // Legacy format (text-only SSE from old endpoint)
        else if (parsed.text && !parsed.type) {
          fullText += parsed.text;
          onTextChunk(fullText);
        }
      } catch (e) {
        if (e instanceof SyntaxError) continue;
        throw e;
      }
    }
  }

  return {text: fullText, functionCalls};
}

// ---------------------------------------------------------------------------
// AI Chat Panel
// ---------------------------------------------------------------------------

function AIChatPanel() {
  const ctx = useSaaSEditor()!;
  const {
    supabase,
    projectId,
    scenes: initialScenes,
    width,
    height,
    fps,
    audioAnalyses,
    audioClips,
    onSceneCreated,
  } = ctx;

  const [prompt, setPrompt] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Keep mutable refs so tool executor always sees latest data
  const scenesRef = useRef<Scene[]>([...initialScenes]);
  const audioAnalysesRef = useRef<Record<string, unknown>>(audioAnalyses ?? {});
  const audioClipsRef = useRef(audioClips ?? []);

  useEffect(() => {
    scenesRef.current = [...initialScenes];
  }, [initialScenes]);

  useEffect(() => {
    audioAnalysesRef.current = audioAnalyses ?? {};
  }, [audioAnalyses]);

  useEffect(() => {
    audioClipsRef.current = audioClips ?? [];
  }, [audioClips]);

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
  }, [messages]);

  const addMessage = useCallback(
    (role: ChatMessage['role'], content: string, blocks?: ContentBlock[]) => {
      setMessages(prev => [
        ...prev,
        {id: nextId(), role, content, blocks},
      ]);
    },
    [],
  );

  const updateLastAiMessage = useCallback(
    (
      updater: (msg: ChatMessage) => ChatMessage,
    ) => {
      setMessages(prev => {
        const idx = prev.length - 1;
        if (idx >= 0 && prev[idx].role === 'ai') {
          const updated = [...prev];
          updated[idx] = updater(updated[idx]);
          return updated;
        }
        return prev;
      });
    },
    [],
  );

  const handleStop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const handleSubmit = async () => {
    if (!prompt.trim() || streaming) return;

    const userPrompt = prompt;
    setPrompt('');
    addMessage('user', userPrompt);
    setStreaming(true);

    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    const abort = new AbortController();
    abortRef.current = abort;

    // Create the AI message placeholder
    const aiMsgId = nextId();
    setMessages(prev => [
      ...prev,
      {id: aiMsgId, role: 'ai', content: '', blocks: []},
    ]);

    try {
      // Build system prompts
      const fullSystem = buildScenePrompt(width, height, fps);
      const continuationSystem = buildContinuationPrompt(width, height, fps);

      // Inject audio summary if available (use refs for latest data)
      let systemPrompt = fullSystem;
      const currentAudioAnalyses = audioAnalysesRef.current;
      const currentAudioClips = audioClipsRef.current;
      if (currentAudioAnalyses && Object.keys(currentAudioAnalyses).length > 0) {
        const audioSummary = buildAudioSummary(currentAudioAnalyses, currentAudioClips);
        if (audioSummary) {
          systemPrompt += audioSummary;
        }
      }

      // Build Gemini conversation history
      const geminiHistory: GeminiMessage[] = [];

      // Include recent UI messages as context (last 10, excluding current)
      const recentUiMsgs = messages.slice(-10);
      for (const m of recentUiMsgs) {
        if (m.role === 'user') {
          geminiHistory.push({role: 'user', parts: [{text: m.content}]});
        } else if (m.role === 'ai' && m.content) {
          geminiHistory.push({role: 'model', parts: [{text: m.content}]});
        }
      }

      // Add current user message. Audio transcript is in the system prompt
      // via buildAudioSummary. The AI should call get_audio_analysis for
      // detailed word timings when needed.
      geminiHistory.push({role: 'user', parts: [{text: userPrompt}]});

      // Build tool context for executor
      const toolCtx: ToolContext = {
        supabase,
        projectId,
        scenes: scenesRef.current,
        width,
        height,
        fps,
        audioAnalyses: audioAnalysesRef.current,
        audioClips: audioClipsRef.current,
        onScenesChanged: () => {
          onSceneCreated();
        },
      };

      let accumulatedText = '';
      const allBlocks: ContentBlock[] = [];

      // --- Agentic loop ---
      for (let iteration = 0; iteration < MAX_ITERATIONS; iteration++) {
        if (abort.signal.aborted) break;

        // After first iteration, use condensed prompt
        const currentSystem = iteration === 0 ? systemPrompt : continuationSystem;

        // Compress old tool results before calling API
        if (iteration > 0) {
          compressOldToolResults(geminiHistory);
        }

        // Stream the Gemini response
        const result = await streamGeminiResponse(
          currentSystem,
          geminiHistory,
          SAAS_TOOL_DECLARATIONS,
          abort.signal,
          (fullText: string) => {
            // Update the AI message with streaming text
            setMessages(prev => {
              const idx = prev.findIndex(m => m.id === aiMsgId);
              if (idx === -1) return prev;
              const updated = [...prev];
              updated[idx] = {
                ...updated[idx],
                content: accumulatedText + fullText.slice(accumulatedText.length > 0 ? 0 : 0),
              };
              return updated;
            });
          },
        );

        // Accumulate text
        if (result.text) {
          accumulatedText += result.text;
          allBlocks.push({type: 'text', text: result.text});

          setMessages(prev => {
            const idx = prev.findIndex(m => m.id === aiMsgId);
            if (idx === -1) return prev;
            const updated = [...prev];
            updated[idx] = {
              ...updated[idx],
              content: accumulatedText,
              blocks: [...allBlocks],
            };
            return updated;
          });
        }

        // Push model response to history
        const modelParts: Array<Record<string, unknown>> = [];
        if (result.text) {
          modelParts.push({text: result.text});
        }
        for (const fc of result.functionCalls) {
          const part: Record<string, unknown> = {
            functionCall: {name: fc.name, args: fc.args},
          };
          // Gemini 3.x requires thoughtSignature for reasoning continuity
          if (fc.thoughtSignature) {
            part.thoughtSignature = fc.thoughtSignature;
          }
          modelParts.push(part);
        }
        if (modelParts.length > 0) {
          geminiHistory.push({role: 'model', parts: modelParts});
        }

        // If no function calls, we're done
        if (result.functionCalls.length === 0) break;

        // Execute each function call
        const functionResponseParts: Array<Record<string, unknown>> = [];

        for (const fc of result.functionCalls) {
          if (abort.signal.aborted) break;

          // Show running activity
          const actId = nextId('act');
          const activity: ToolActivity = {
            id: actId,
            toolName: fc.name,
            status: 'running',
            summary: runningSummary(fc.name, fc.args),
          };
          allBlocks.push({type: 'activity', activity});

          setMessages(prev => {
            const idx = prev.findIndex(m => m.id === aiMsgId);
            if (idx === -1) return prev;
            const updated = [...prev];
            updated[idx] = {
              ...updated[idx],
              content: accumulatedText,
              blocks: [...allBlocks],
            };
            return updated;
          });

          // Execute the tool
          const toolResult = await executeSaaSTool(fc.name, fc.args, toolCtx);

          // Update activity to done
          const doneActivity: ToolActivity = {
            ...activity,
            status: toolResult.isError ? 'error' : 'done',
            summary: doneSummary(fc.name, fc.args, toolResult),
          };

          // Update the activity in blocks
          const blockIdx = allBlocks.findIndex(
            b => b.type === 'activity' && b.activity.id === actId,
          );
          if (blockIdx !== -1) {
            allBlocks[blockIdx] = {type: 'activity', activity: doneActivity};
          }

          setMessages(prev => {
            const idx = prev.findIndex(m => m.id === aiMsgId);
            if (idx === -1) return prev;
            const updated = [...prev];
            updated[idx] = {
              ...updated[idx],
              blocks: [...allBlocks],
            };
            return updated;
          });

          // Build function response for Gemini
          functionResponseParts.push({
            functionResponse: {
              name: fc.name,
              response: {content: toolResult.content},
            },
          });
        }

        // Push all function responses as a single function turn
        if (functionResponseParts.length > 0) {
          geminiHistory.push({
            role: 'function',
            parts: functionResponseParts,
          });
        }
      }

      // Finalize
      setMessages(prev => {
        const idx = prev.findIndex(m => m.id === aiMsgId);
        if (idx === -1) return prev;
        const updated = [...prev];
        updated[idx] = {
          ...updated[idx],
          content: accumulatedText || 'Done.',
          blocks: [...allBlocks],
        };
        return updated;
      });
    } catch (err) {
      if (abort.signal.aborted) {
        // User stopped -- leave partial content
        setMessages(prev => {
          const idx = prev.findIndex(m => m.id === aiMsgId);
          if (idx === -1) return prev;
          const updated = [...prev];
          updated[idx] = {
            ...updated[idx],
            content: updated[idx].content || 'Generation stopped.',
          };
          return updated;
        });
      } else {
        addMessage(
          'error',
          err instanceof Error ? err.message : 'Unknown error',
        );
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const handleTextareaInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPrompt(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 100) + 'px';
  };

  return (
    <div className={styles.chat}>
      {/* Messages */}
      <div className={styles.chatMessages}>
        {messages.length === 0 ? (
          <div className={styles.chatEmpty}>
            <div className={styles.chatEmptyIcon}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 3L14.5 8.5L20 12L14.5 15.5L12 21L9.5 15.5L4 12L9.5 8.5L12 3Z" />
              </svg>
            </div>
            <p className={styles.chatEmptyText}>
              Describe the animation you want to create.
            </p>
          </div>
        ) : (
          messages.map(msg => (
            <div
              key={msg.id}
              className={`${styles.chatMsg} ${styles[`chatMsg_${msg.role}`]}`}
            >
              {msg.role === 'success' && (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={styles.chatMsgIcon}
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
              {/* Render blocks if present (AI messages with tool activities) */}
              {msg.blocks && msg.blocks.length > 0 ? (
                <div className={styles.chatBlocks}>
                  {msg.blocks.map((block, i) => {
                    if (block.type === 'text') {
                      return (
                        <div key={`text-${i}`} className={styles.chatBlockText}>
                          {block.text}
                        </div>
                      );
                    }
                    const a = block.activity;
                    return (
                      <div
                        key={a.id}
                        className={`${styles.toolActivity} ${
                          a.status === 'running'
                            ? styles.toolActivityRunning
                            : a.status === 'error'
                              ? styles.toolActivityError
                              : styles.toolActivityDone
                        }`}
                      >
                        <span className={styles.toolActivityIcon}>
                          {a.status === 'running' && (
                            <svg viewBox="0 0 16 16" fill="none">
                              <circle
                                cx="8"
                                cy="8"
                                r="6"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeDasharray="20 12"
                              />
                            </svg>
                          )}
                          {a.status === 'done' && (
                            <svg
                              viewBox="0 0 16 16"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="3 8 6.5 11.5 13 5" />
                            </svg>
                          )}
                          {a.status === 'error' && (
                            <svg
                              viewBox="0 0 16 16"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                            >
                              <line x1="4" y1="4" x2="12" y2="12" />
                              <line x1="12" y1="4" x2="4" y2="12" />
                            </svg>
                          )}
                        </span>
                        <span className={styles.toolActivityText}>
                          {a.summary}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                msg.content
              )}
            </div>
          ))
        )}
        {streaming && (
          <div className={styles.chatStreaming}>
            <div className={styles.chatDots}>
              <div className={styles.chatDot} />
              <div className={styles.chatDot} />
              <div className={styles.chatDot} />
            </div>
            <span>Generating...</span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className={styles.chatInputArea}>
        <div className={styles.chatInputWrap}>
          <textarea
            ref={textareaRef}
            className={styles.chatTextarea}
            value={prompt}
            onChange={handleTextareaInput}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder="Describe your animation..."
            rows={1}
          />
          {streaming ? (
            <button
              className={styles.chatStopBtn}
              onClick={handleStop}
              title="Stop generation"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="6" width="12" height="12" rx="2" />
              </svg>
            </button>
          ) : (
            <button
              className={styles.chatSendBtn}
              onClick={handleSubmit}
              disabled={!prompt.trim()}
              title="Send"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Threads (exported component)
// ---------------------------------------------------------------------------

export function Threads() {
  const saas = useSaaSEditor();

  return (
    <Pane title="AI Assistant" id="threads-pane">
      {saas ? (
        <AIChatPanel />
      ) : (
        <div
          style={{
            padding: 16,
            fontSize: 13,
            color: 'rgba(255,255,255,0.3)',
          }}
        >
          AI Assistant is available in Cascade Cloud.
        </div>
      )}
    </Pane>
  );
}
